import React, { useState } from 'react';
import { format, subMonths } from 'date-fns';
import { 
  QuestionMarkCircleIcon, 
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { addSongMetrics } from '../../services/songService';

interface AddSongMetricsProps {
  onClose: () => void;
  onSubmit: (success: boolean, newData?: any) => void;
  songId: string;
  songTitle: string;
}

const AddSongMetrics: React.FC<AddSongMetricsProps> = ({ 
  onClose, 
  onSubmit, 
  songId, 
  songTitle 
}) => {
  // State for month/year selection
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const formattedDate = format(selectedDate, 'MMMM yyyy');
  const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  const [formData, setFormData] = useState({
    streams: '',
    listeners: '',
    saves: '',
    playlist_adds: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Navigate to previous month
  const handlePreviousMonth = () => {
    setSelectedDate(prevDate => subMonths(prevDate, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    // Don't allow selecting future months
    const nextMonth = new Date(selectedDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    if (nextMonth <= new Date()) {
      setSelectedDate(nextMonth);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Reset error when input changes
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Convert form data to appropriate types
      const metricsData = {
        streams: parseInt(formData.streams, 10) || 0,
        listeners: parseInt(formData.listeners, 10) || 0,
        saves: parseInt(formData.saves, 10) || 0,
        playlist_adds: parseInt(formData.playlist_adds, 10) || 0,
        notes: formData.notes,
        // Add the selected month in YYYY-MM-01 format
        metric_date: format(selectedDate, 'yyyy-MM-01')
      };
      
      // Send data to the API
      const result = await addSongMetrics(songId, metricsData);
      
      // If successful, call the onSubmit callback
      if (result && result.success) {
        onSubmit(true, result.data);
      } else {
        setError('Failed to save metrics. Please try again.');
        onSubmit(false);
      }
    } catch (err) {
      console.error('Error submitting song metrics:', err);
      setError('An error occurred while saving the metrics.');
      onSubmit(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Song Metrics for "{songTitle}"
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        {/* Month/Year Selector */}
        <div className="mb-6 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-3">
          <button 
            onClick={handlePreviousMonth}
            className="p-1 rounded-full hover:bg-gray-200"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          
          <div className="flex items-center">
            <CalendarIcon className="h-5 w-5 text-gray-500 mr-2" />
            <span className="text-gray-700 font-medium">{capitalizedDate}</span>
            <div className="ml-2 text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
              Metrics for last 28 days
            </div>
          </div>
          
          <button 
            onClick={handleNextMonth}
            className="p-1 rounded-full hover:bg-gray-200"
            disabled={selectedDate.getMonth() === new Date().getMonth() && 
                      selectedDate.getFullYear() === new Date().getFullYear()}
          >
            <ChevronRightIcon className={`h-5 w-5 ${selectedDate.getMonth() === new Date().getMonth() && 
                                         selectedDate.getFullYear() === new Date().getFullYear() ? 
                                         'text-gray-300' : 'text-gray-600'}`} />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Enter Spotify metrics for {capitalizedDate}.
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Streams */}
            <div>
              <label htmlFor="streams" className="block text-sm font-medium text-gray-700 mb-1">
                Streams
                <Tooltip text="Total number of streams for this song in the last month." />
              </label>
              <input
                type="number"
                id="streams"
                name="streams"
                value={formData.streams}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="e.g. 5000"
                required
              />
            </div>

            {/* Listeners */}
            <div>
              <label htmlFor="listeners" className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Listeners
                <Tooltip text="Unique listeners who played the song in the last month." />
              </label>
              <input
                type="number"
                id="listeners"
                name="listeners"
                value={formData.listeners}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="e.g. 1200"
                required
              />
            </div>

            {/* Saves */}
            <div>
              <label htmlFor="saves" className="block text-sm font-medium text-gray-700 mb-1">
                Saves
                <Tooltip text="How many times the song was saved to libraries." />
              </label>
              <input
                type="number"
                id="saves"
                name="saves"
                value={formData.saves}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="e.g. 120"
                required
              />
            </div>

            {/* Playlist Adds */}
            <div>
              <label htmlFor="playlist_adds" className="block text-sm font-medium text-gray-700 mb-1">
                Playlist Adds
                <Tooltip text="How many playlists added this song." />
              </label>
              <input
                type="number"
                id="playlist_adds"
                name="playlist_adds"
                value={formData.playlist_adds}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="e.g. 8"
                required
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                placeholder="e.g. Promoted on social media, featured in podcast, etc."
              />
            </div>
          </div>

          <div className="mt-5 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Tooltip Component
interface TooltipProps {
  text: string;
}

const Tooltip: React.FC<TooltipProps> = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span className="relative ml-1 inline-block">
      <QuestionMarkCircleIcon
        className="h-4 w-4 text-gray-400 inline cursor-pointer"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      />
      {isVisible && (
        <div className="absolute z-10 w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg -left-32 bottom-6">
          {text}
          <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 -bottom-1 left-32"></div>
        </div>
      )}
    </span>
  );
};

export default AddSongMetrics;